import type { PlayerInput } from '../types';
import type { SimEvent } from '../sim/events';
import { QUANT, quantI8, dequantI8, quantAngleU16, dequantAngleU16 } from './quantize';
import { BufWriter, BufReader, writeSimEvent, readSimEvent } from './events';
import { ProtocolError } from '../errors';

// Wire message codecs (§9.2). Binary WS, little-endian, first byte = MsgType. PROTOCOL_VERSION is
// checked on Hello (§9.1). Snapshot/state codecs live in codecs.ts + snapshot.ts.

// D122: 9 → 10. Another MatchEvent layout change — OUT_OF_PLAY grew a trailing `taker` byte and
// WHISTLE gained a 'RESTART' kind (append-only, index-stable). Same skew story as D119 below.
// D119: 8 → 9. A real LAYOUT change — MatchEvent's POSSESSION variant grew a trailing `why` byte
// (PossessionWhy, append-only enum). A pre-D119 client reads the entity byte and then hits the reader's
// trailing-bytes check, so the skew is loud rather than silent; a post-D119 client against an old server
// would run off the end of the buffer. Either way the handshake must refuse it first.
// (D110 (HB-P7) was the previous bump, 7 → 8: no layout change, a deploy-together gate for the LOFT
// input bit — a pre-D110 server masked bit 17 and silently flattened crosses. D82 before that: the
// BLOCK ActionKind on the wire.)
export const PROTOCOL_VERSION = 10;

export const MSG = {
  JSON: 0x00,
  HELLO: 0x01,
  INPUT_FRAME: 0x02,
  SWITCH_REQUEST: 0x03,
  PING: 0x04,
  WELCOME: 0x81,
  SNAPSHOT: 0x82,
  SWITCH_ASSIGN: 0x83,
  PONG: 0x84,
  MATCH_EVENT: 0x85,
} as const;

// D45 Item 6: buttons widened u16 → u32 to fit BTN.TRAP (bit 16) — the u16 field was fully assigned.
export const PACKED_INPUT_SIZE = 10; // moveX i8, moveY i8, aimYaw u16, aimElev i8, buttons u32, emote u8

/** Write a 10-byte PackedInput (§9.2) at `off` in a DataView. */
export function writePackedInput(view: DataView, off: number, inp: PlayerInput): void {
  view.setInt8(off + 0, quantI8(inp.moveX, QUANT.MOVE_LSB));
  view.setInt8(off + 1, quantI8(inp.moveY, QUANT.MOVE_LSB));
  view.setUint16(off + 2, quantAngleU16(inp.aimYaw), true);
  view.setInt8(off + 4, quantI8(inp.aimElev, QUANT.MOVE_LSB));
  view.setUint32(off + 5, inp.buttons >>> 0, true);
  view.setUint8(off + 9, inp.emote & 0xff);
}

/** Read a PackedInput at `off`. */
export function readPackedInput(view: DataView, off: number): PlayerInput {
  return {
    moveX: dequantI8(view.getInt8(off + 0), QUANT.MOVE_LSB),
    moveY: dequantI8(view.getInt8(off + 1), QUANT.MOVE_LSB),
    aimYaw: dequantAngleU16(view.getUint16(off + 2, true)),
    aimElev: dequantI8(view.getInt8(off + 4), QUANT.MOVE_LSB),
    buttons: view.getUint32(off + 5, true),
    emote: view.getUint8(off + 9),
  };
}

export function encodePackedInput(inp: PlayerInput): Uint8Array {
  const b = new Uint8Array(PACKED_INPUT_SIZE);
  writePackedInput(new DataView(b.buffer), 0, inp);
  return b;
}
export function decodePackedInput(bytes: Uint8Array): PlayerInput {
  return readPackedInput(new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength), 0);
}

// ─────────────────────────── InputFrame (0x02, §9.2 C→S) ───────────────────────────
// Layout: 0x02 | firstTick u32 | count u8 (≤3) | count × PackedInput(8B) | lastSnapTick u32 (ack).
// The ×3 redundancy re-carries the last inputs so a late frame / reconnect heals gaps (§10.6);
// the server dedups by tick. `lastSnapTick` = highest snapshot tick the client decoded (ack, §9.3).

export const MAX_INPUT_FRAME_COUNT = 3;

export interface InputFrame {
  firstTick: number;
  inputs: PlayerInput[]; // for ticks firstTick .. firstTick+inputs.length-1
  lastSnapTick: number;
}

export function encodeInputFrame(f: InputFrame): Uint8Array {
  const count = f.inputs.length;
  if (count > MAX_INPUT_FRAME_COUNT) throw new ProtocolError('InputFrame: count > 3');
  const b = new Uint8Array(1 + 4 + 1 + count * PACKED_INPUT_SIZE + 4);
  const v = new DataView(b.buffer);
  v.setUint8(0, MSG.INPUT_FRAME);
  v.setUint32(1, f.firstTick >>> 0, true);
  v.setUint8(5, count);
  for (let i = 0; i < count; i++) writePackedInput(v, 6 + i * PACKED_INPUT_SIZE, f.inputs[i]!);
  v.setUint32(6 + count * PACKED_INPUT_SIZE, f.lastSnapTick >>> 0, true);
  return b;
}

/** Strict parse — any malformed frame throws ProtocolError (feeds the InputHub strike counter, T-NET-010). */
export function decodeInputFrame(bytes: Uint8Array): InputFrame {
  if (bytes.length < 6) throw new ProtocolError('InputFrame: too short');
  const v = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  if (v.getUint8(0) !== MSG.INPUT_FRAME) throw new ProtocolError('InputFrame: bad msg type');
  const firstTick = v.getUint32(1, true);
  const count = v.getUint8(5);
  if (count > MAX_INPUT_FRAME_COUNT) throw new ProtocolError('InputFrame: count > 3');
  const expected = 1 + 4 + 1 + count * PACKED_INPUT_SIZE + 4;
  if (bytes.length !== expected) throw new ProtocolError('InputFrame: bad length');
  const inputs: PlayerInput[] = [];
  for (let i = 0; i < count; i++) inputs.push(readPackedInput(v, 6 + i * PACKED_INPUT_SIZE));
  const lastSnapTick = v.getUint32(6 + count * PACKED_INPUT_SIZE, true);
  return { firstTick, inputs, lastSnapTick };
}

// ─────────────────────────── manual UTF-8 (keeps shared free of DOM/node types) ───────────────────────────

function utf8Encode(s: string): number[] {
  const out: number[] = [];
  for (let i = 0; i < s.length; i++) {
    let c = s.charCodeAt(i);
    if (c < 0x80) out.push(c);
    else if (c < 0x800) out.push(0xc0 | (c >> 6), 0x80 | (c & 0x3f));
    else if (c >= 0xd800 && c <= 0xdbff) {
      const c2 = s.charCodeAt(++i);
      c = 0x10000 + ((c - 0xd800) << 10) + (c2 - 0xdc00);
      out.push(0xf0 | (c >> 18), 0x80 | ((c >> 12) & 0x3f), 0x80 | ((c >> 6) & 0x3f), 0x80 | (c & 0x3f));
    } else out.push(0xe0 | (c >> 12), 0x80 | ((c >> 6) & 0x3f), 0x80 | (c & 0x3f));
  }
  return out;
}
function utf8Decode(bytes: Uint8Array): string {
  let s = '';
  for (let i = 0; i < bytes.length; ) {
    const b = bytes[i++]!;
    let cp: number;
    if (b < 0x80) cp = b;
    else if (b < 0xe0) cp = ((b & 0x1f) << 6) | (bytes[i++]! & 0x3f);
    else if (b < 0xf0) cp = ((b & 0x0f) << 12) | ((bytes[i++]! & 0x3f) << 6) | (bytes[i++]! & 0x3f);
    else {
      cp = ((b & 0x07) << 18) | ((bytes[i++]! & 0x3f) << 12) | ((bytes[i++]! & 0x3f) << 6) | (bytes[i++]! & 0x3f);
      cp -= 0x10000;
      s += String.fromCharCode(0xd800 + (cp >> 10), 0xdc00 + (cp & 0x3ff));
      continue;
    }
    s += String.fromCharCode(cp);
  }
  return s;
}

// ─────────────────────────── Hello (0x01, §9.2 / T-PROTO-007) ───────────────────────────

export interface Hello {
  ver: number;
  flags: number;
  gameSessionToken: Uint8Array; // 16B
  guestId: Uint8Array; // 16B (ULID bytes)
  name: string; // ≤ 24B utf8
}

export function encodeHello(h: Hello): Uint8Array {
  const nameBytes = utf8Encode(h.name).slice(0, 24); // §9.2: utf8 ≤ 24B — match decodeHello so the codec round-trips
  const b = new Uint8Array(1 + 2 + 1 + 16 + 16 + 1 + nameBytes.length);
  const v = new DataView(b.buffer);
  v.setUint8(0, MSG.HELLO);
  v.setUint16(1, h.ver, true);
  v.setUint8(3, h.flags);
  b.set(h.gameSessionToken.subarray(0, 16), 4);
  b.set(h.guestId.subarray(0, 16), 20);
  v.setUint8(36, nameBytes.length);
  b.set(nameBytes, 37);
  return b;
}

/** Parse a Hello. Throws ProtocolError('VERSION_MISMATCH') if `ver` ≠ PROTOCOL_VERSION (§9.1). */
export function decodeHello(bytes: Uint8Array): Hello {
  if (bytes.length < 37) throw new ProtocolError('Hello: too short');
  const v = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  if (v.getUint8(0) !== MSG.HELLO) throw new ProtocolError('Hello: bad msg type');
  const ver = v.getUint16(1, true);
  if (ver !== PROTOCOL_VERSION) throw new ProtocolError('VERSION_MISMATCH');
  const flags = v.getUint8(3);
  const gameSessionToken = bytes.slice(4, 20);
  const guestId = bytes.slice(20, 36);
  const nameLen = v.getUint8(36);
  if (37 + nameLen > bytes.length || nameLen > 24) throw new ProtocolError('Hello: bad name length');
  const name = utf8Decode(bytes.subarray(37, 37 + nameLen));
  return { ver, flags, gameSessionToken, guestId, name };
}

// ─────────────────────────── Welcome (0x81) + JSON envelope (0x00, §9.2) ───────────────────────────

export interface Welcome {
  ver: number;
  gameSessionToken: Uint8Array; // 16B
  roomStateJson: string; // {mode, slots, simConfig, simConfigHash} — the predictor's cfg source (§17)
}
export function encodeWelcome(w: Welcome): Uint8Array {
  const json = utf8Encode(w.roomStateJson);
  const b = new Uint8Array(1 + 2 + 16 + json.length);
  const v = new DataView(b.buffer);
  v.setUint8(0, MSG.WELCOME);
  v.setUint16(1, w.ver, true);
  b.set(w.gameSessionToken.subarray(0, 16), 3);
  b.set(json, 19);
  return b;
}
export function decodeWelcome(bytes: Uint8Array): Welcome {
  if (bytes.length < 19) throw new ProtocolError('Welcome: too short');
  const v = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  if (v.getUint8(0) !== MSG.WELCOME) throw new ProtocolError('Welcome: bad msg type');
  return { ver: v.getUint16(1, true), gameSessionToken: bytes.slice(3, 19), roomStateJson: utf8Decode(bytes.subarray(19)) };
}

/** JSON envelope (0x00): a `{t, ...}` control message (LOBBY_STATE, MATCH_START, ERROR, JOIN_ROOM, START, …). */
export interface JsonMsg {
  t: string;
  [k: string]: unknown;
}
export function encodeJsonMsg(m: JsonMsg): Uint8Array {
  const json = utf8Encode(JSON.stringify(m));
  const b = new Uint8Array(1 + json.length);
  b[0] = MSG.JSON;
  b.set(json, 1);
  return b;
}
export function decodeJsonMsg(bytes: Uint8Array): JsonMsg {
  if (bytes.length < 1 || bytes[0] !== MSG.JSON) throw new ProtocolError('JsonMsg: bad msg type');
  let parsed: unknown;
  try {
    parsed = JSON.parse(utf8Decode(bytes.subarray(1)));
  } catch {
    throw new ProtocolError('JsonMsg: invalid JSON');
  }
  if (typeof parsed !== 'object' || parsed === null || typeof (parsed as JsonMsg).t !== 'string') {
    throw new ProtocolError('JsonMsg: missing discriminant');
  }
  return parsed as JsonMsg;
}

// ─────────────────────────── SwitchAssign (0x83), Pong (0x84), MatchEvent (0x85) ───────────────────────────

export interface SwitchAssign {
  sessionSlot: number;
  entityId: number;
  effectiveTick: number;
  /** D36: ticks after effectiveTick during which the BOT still drives the entity (0 = instant control). */
  handoverTicks: number;
}
export function encodeSwitchAssign(s: SwitchAssign): Uint8Array {
  const b = new Uint8Array(8);
  const v = new DataView(b.buffer);
  v.setUint8(0, MSG.SWITCH_ASSIGN);
  v.setUint8(1, s.sessionSlot);
  v.setUint8(2, s.entityId);
  v.setUint32(3, s.effectiveTick, true);
  v.setUint8(7, s.handoverTicks);
  return b;
}
export function decodeSwitchAssign(bytes: Uint8Array): SwitchAssign {
  if (bytes.length !== 8) throw new ProtocolError('SwitchAssign: bad length');
  const v = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  if (v.getUint8(0) !== MSG.SWITCH_ASSIGN) throw new ProtocolError('SwitchAssign: bad msg type');
  return { sessionSlot: v.getUint8(1), entityId: v.getUint8(2), effectiveTick: v.getUint32(3, true), handoverTicks: v.getUint8(7) };
}

// ─────────────────────────── SwitchRequest (0x03, C→S) ───────────────────────────
export const SWITCH_CYCLE = 0xff; // target 255 = cycle to the next controllable entity (§8.2)

export interface SwitchRequest {
  target: number; // entityId, or SWITCH_CYCLE
}
export function encodeSwitchRequest(s: SwitchRequest): Uint8Array {
  return new Uint8Array([MSG.SWITCH_REQUEST, s.target & 0xff]);
}
export function decodeSwitchRequest(bytes: Uint8Array): SwitchRequest {
  if (bytes.length !== 2) throw new ProtocolError('SwitchRequest: bad length');
  if (bytes[0] !== MSG.SWITCH_REQUEST) throw new ProtocolError('SwitchRequest: bad msg type');
  return { target: bytes[1]! };
}

export interface Ping {
  clientTimeMs: number;
}
export function encodePing(p: Ping): Uint8Array {
  const b = new Uint8Array(9);
  const v = new DataView(b.buffer);
  v.setUint8(0, MSG.PING);
  v.setFloat64(1, p.clientTimeMs, true);
  return b;
}
export function decodePing(bytes: Uint8Array): Ping {
  if (bytes.length !== 9) throw new ProtocolError('Ping: bad length');
  const v = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  if (v.getUint8(0) !== MSG.PING) throw new ProtocolError('Ping: bad msg type');
  return { clientTimeMs: v.getFloat64(1, true) };
}

export interface Pong {
  clientTimeMs: number;
  serverTimeMs: number;
  serverTick: number;
}
export function encodePong(p: Pong): Uint8Array {
  const b = new Uint8Array(21);
  const v = new DataView(b.buffer);
  v.setUint8(0, MSG.PONG);
  v.setFloat64(1, p.clientTimeMs, true);
  v.setFloat64(9, p.serverTimeMs, true);
  v.setUint32(17, p.serverTick, true);
  return b;
}
export function decodePong(bytes: Uint8Array): Pong {
  if (bytes.length !== 21) throw new ProtocolError('Pong: bad length');
  const v = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  if (v.getUint8(0) !== MSG.PONG) throw new ProtocolError('Pong: bad msg type');
  return { clientTimeMs: v.getFloat64(1, true), serverTimeMs: v.getFloat64(9, true), serverTick: v.getUint32(17, true) };
}

export interface MatchEventMsg {
  tick: number;
  idxWithinTick: number;
  event: SimEvent;
}
export function encodeMatchEvent(m: MatchEventMsg): Uint8Array {
  const w = new BufWriter();
  w.u8(MSG.MATCH_EVENT);
  w.u32(m.tick);
  w.u8(m.idxWithinTick);
  writeSimEvent(w, m.event);
  return w.bytes();
}
export function decodeMatchEvent(bytes: Uint8Array): MatchEventMsg {
  const r = new BufReader(new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength), bytes.byteLength);
  if (r.u8() !== MSG.MATCH_EVENT) throw new ProtocolError('MatchEvent: bad msg type');
  const tick = r.u32();
  const idxWithinTick = r.u8();
  const event = readSimEvent(r);
  if (!r.atEnd) throw new ProtocolError('MatchEvent: trailing bytes');
  return { tick, idxWithinTick, event };
}
